import { useEffect, useMemo, useState } from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import CircularProgress from '@mui/material/CircularProgress';
import TextField from '@mui/material/TextField';
import { v4 as uuidv4 } from 'uuid';
import { resolveClientLocale } from 'lib/i18n/browser';
import { useLocale } from 'lib/i18n/client';
import { getTranslations } from 'lib/i18n/dictionary';

import {
  fetchCategories,
  searchCategories,
  type ProfileCategory,
} from './profile.api';

interface CategorySelectProps {
  accessToken: string | null;
  value: ProfileCategory[];
  onChange: (categories: ProfileCategory[]) => void;
  disabled?: boolean;
}

function normalizeBrowserLocale() {
  return resolveClientLocale();
}

function normalizeCategoryTitle(value: string) {
  return value.trim().replace(/\s+/g, ' ').slice(0, 128);
}

function normalizeCategoryKey(value: string) {
  const normalizedValue = value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

  return normalizedValue.slice(0, 64);
}

function getCategoryIdentity(category: ProfileCategory) {
  const normalizedKey = normalizeCategoryKey(category.key);
  if (normalizedKey.length) {
    return `key:${normalizedKey}`;
  }

  const normalizedTitle = normalizeCategoryTitle(category.title).toLowerCase();
  if (normalizedTitle.length) {
    return `title:${normalizedTitle}`;
  }

  return `uuid:${category.uuid.trim()}`;
}

function normalizeCategory(category: ProfileCategory): ProfileCategory | null {
  const normalizedTitle = normalizeCategoryTitle(category.title);
  if (!normalizedTitle) {
    return null;
  }

  const normalizedUuid = category.uuid.trim() || uuidv4();
  const titleKey = normalizeCategoryKey(normalizedTitle);
  const normalizedKey =
    normalizeCategoryKey(category.key) ||
    titleKey ||
    `category-${normalizedUuid.replace(/-/g, '').slice(0, 12)}`;

  return {
    uuid: normalizedUuid,
    key: normalizedKey,
    title: normalizedTitle,
  };
}

function resolveTypedCategory(
  rawValue: string,
  categoriesPool: ProfileCategory[]
): ProfileCategory | null {
  const normalizedTitle = normalizeCategoryTitle(rawValue);
  if (!normalizedTitle) {
    return null;
  }

  const normalizedKey = normalizeCategoryKey(normalizedTitle);
  const normalizedTitleLowerCase = normalizedTitle.toLowerCase();

  const matchedCategory = categoriesPool.find((category) => {
    const categoryTitle = normalizeCategoryTitle(category.title).toLowerCase();
    const categoryKey = normalizeCategoryKey(category.key);

    return (
      categoryTitle === normalizedTitleLowerCase ||
      (normalizedKey.length > 0 && categoryKey === normalizedKey)
    );
  });

  if (matchedCategory) {
    return normalizeCategory(matchedCategory);
  }

  const generatedUuid = uuidv4();
  return {
    uuid: generatedUuid,
    key: normalizedKey || `category-${generatedUuid.replace(/-/g, '').slice(0, 12)}`,
    title: normalizedTitle,
  };
}

function normalizeSelectedCategories(
  selectedCategories: Array<ProfileCategory | string>,
  categoriesPool: ProfileCategory[]
) {
  const normalizedPoolByIdentity = new Map<string, ProfileCategory>();
  for (const category of categoriesPool) {
    const normalizedCategory = normalizeCategory(category);
    if (!normalizedCategory) {
      continue;
    }

    const categoryIdentity = getCategoryIdentity(normalizedCategory);
    if (normalizedPoolByIdentity.has(categoryIdentity)) {
      continue;
    }

    normalizedPoolByIdentity.set(categoryIdentity, normalizedCategory);
  }

  const uniqueCategories = new Map<string, ProfileCategory>();

  for (const selectedCategory of selectedCategories) {
    const normalizedCategory =
      typeof selectedCategory === 'string'
        ? resolveTypedCategory(selectedCategory, categoriesPool)
        : normalizeCategory(selectedCategory);

    if (!normalizedCategory) {
      continue;
    }

    const categoryIdentity = getCategoryIdentity(normalizedCategory);
    if (uniqueCategories.has(categoryIdentity)) {
      continue;
    }

    uniqueCategories.set(
      categoryIdentity,
      normalizedPoolByIdentity.get(categoryIdentity) ?? normalizedCategory
    );
  }

  return Array.from(uniqueCategories.values());
}

function mergeCategoryOptions(primary: ProfileCategory[], secondary: ProfileCategory[]) {
  const optionsByIdentity = new Map<string, ProfileCategory>();

  for (const category of primary) {
    const normalizedCategory = normalizeCategory(category);
    if (!normalizedCategory) {
      continue;
    }

    const categoryIdentity = getCategoryIdentity(normalizedCategory);
    if (optionsByIdentity.has(categoryIdentity)) {
      continue;
    }

    optionsByIdentity.set(categoryIdentity, normalizedCategory);
  }

  for (const category of secondary) {
    const normalizedCategory = normalizeCategory(category);
    if (!normalizedCategory) {
      continue;
    }

    const categoryIdentity = getCategoryIdentity(normalizedCategory);
    if (optionsByIdentity.has(categoryIdentity)) {
      continue;
    }

    optionsByIdentity.set(categoryIdentity, normalizedCategory);
  }

  return Array.from(optionsByIdentity.values());
}

export function CategorySelect({
  accessToken,
  value,
  onChange,
  disabled = false,
}: CategorySelectProps) {
  const locale = useLocale();
  const t = getTranslations(locale).adminInputs;
  const [query, setQuery] = useState('');
  const [options, setOptions] = useState<ProfileCategory[]>([]);
  const [isOptionsOpen, setIsOptionsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const browserLocale = useMemo(() => normalizeBrowserLocale(), []);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);

    fetchCategories(browserLocale, 50, 0, accessToken ?? undefined)
      .then((categories) => {
        if (cancelled) {
          return;
        }

        setOptions((currentOptions) => mergeCategoryOptions(currentOptions, categories));
      })
      .catch(() => {
        if (!cancelled) {
          setOptions((currentOptions) => currentOptions);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [accessToken, browserLocale]);

  useEffect(() => {
    const normalizedQuery = query.trim();
    if (!normalizedQuery.length) {
      setIsOptionsOpen(false);
      return;
    }

    let cancelled = false;
    const timerId = window.setTimeout(() => {
      setIsLoading(true);

      searchCategories(normalizedQuery, browserLocale, accessToken ?? undefined)
        .then((categories) => {
          if (cancelled) {
            return;
          }

          const normalizedCategories = mergeCategoryOptions(categories, value);
          setOptions((currentOptions) =>
            mergeCategoryOptions(currentOptions, normalizedCategories)
          );
          setIsOptionsOpen(normalizedCategories.length > 0);
        })
        .catch(() => {
          if (!cancelled) {
            setIsOptionsOpen(false);
          }
        })
        .finally(() => {
          if (!cancelled) {
            setIsLoading(false);
          }
        });
    }, 250);

    return () => {
      cancelled = true;
      window.clearTimeout(timerId);
    };
  }, [query, value, accessToken, browserLocale]);

  const combinedOptions = useMemo(
    () => mergeCategoryOptions(options, value),
    [options, value]
  );
  const combinedOptionsByIdentity = useMemo(() => {
    const optionsByIdentity = new Map<string, ProfileCategory>();

    for (const option of combinedOptions) {
      const categoryIdentity = getCategoryIdentity(option);
      if (optionsByIdentity.has(categoryIdentity)) {
        continue;
      }

      optionsByIdentity.set(categoryIdentity, option);
    }

    return optionsByIdentity;
  }, [combinedOptions]);

  useEffect(() => {
    if (!value.length || !combinedOptionsByIdentity.size) {
      return;
    }

    const localizedCategories = value.map((category) => {
      const normalizedCategory = normalizeCategory(category);
      if (!normalizedCategory) {
        return category;
      }

      return (
        combinedOptionsByIdentity.get(getCategoryIdentity(normalizedCategory)) ??
        normalizedCategory
      );
    });

    const hasChanges = localizedCategories.some((category, index) => {
      const currentCategory = value[index];
      return (
        category.uuid !== currentCategory.uuid ||
        category.key !== currentCategory.key ||
        category.title !== currentCategory.title
      );
    });

    if (hasChanges) {
      onChange(localizedCategories);
    }
  }, [combinedOptionsByIdentity, onChange, value]);

  return (
    <Autocomplete<ProfileCategory, true, false, true>
      multiple
      freeSolo
      forcePopupIcon
      options={combinedOptions}
      value={value}
      disabled={disabled}
      loading={isLoading}
      open={isOptionsOpen && combinedOptions.length > 0}
      onOpen={() => {
        if (combinedOptions.length > 0) {
          setIsOptionsOpen(true);
        }
      }}
      onClose={() => {
        setIsOptionsOpen(false);
      }}
      filterOptions={(filteredOptions) => filteredOptions}
      isOptionEqualToValue={(option, selectedCategory) =>
        typeof selectedCategory !== 'string' && option.uuid === selectedCategory.uuid
      }
      getOptionLabel={(option) => (typeof option === 'string' ? option : option.title)}
      onInputChange={(_, nextQuery, reason) => {
        if (reason === 'input') {
          setQuery(nextQuery);
          if (!nextQuery.trim().length) {
            setIsOptionsOpen(false);
          }
        } else if (reason === 'clear' || reason === 'reset') {
          setQuery('');
          setIsOptionsOpen(false);
        }
      }}
      onChange={(_, nextCategories) => {
        const normalizedCategories = normalizeSelectedCategories(
          nextCategories,
          mergeCategoryOptions(options, value)
        );

        onChange(normalizedCategories);
        setOptions((currentOptions) =>
          mergeCategoryOptions(currentOptions, normalizedCategories)
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder={t.searchCategoriesPlaceholder}
          helperText={t.searchCategoriesHelper}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {isLoading ? <CircularProgress color="inherit" size={16} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
}
